import type { LayerSpecification, Map as MlMap } from "maplibre-gl";
import type { LngLat, TileLayerName } from "../types";
import { pointInPolygon, polygonCentroid } from "../../core/pathfinding";
import { lngLatToMercator } from "../../loaders/formats/shared";
import type { CaptureRegionOptions, MercatorBbox } from "./captureRegion";
import { normalizeBbox, planCapture } from "./captureRegion";
import { withOffscreenBasemap } from "./offscreenBasemap";

/**
 * **Les bâtiments du fond de carte OSM, en vecteur** — repris tels quels, jamais redétectés.
 *
 * ## Pourquoi ce module existe
 *
 * L'autre voie pour obtenir des formes depuis la carte est la CAPTURE ({@link captureRegion}) : on
 * rend la zone en PNG et le serveur y détecte des formes par quantification de couleur. Sur un fond
 * OSM, cette voie ne peut pas marcher, et ce n'est pas un réglage à trouver : le style `positron`
 * est du gris sur du gris (fond ≈ `#f8f8f6`, bâti ≈ `#e8e6e2`), très en dessous de la distance de
 * fusion du quantizer — toute la capture se réduit à une seule couleur, donc à zéro forme.
 *
 * Or l'information est déjà là, exacte : le fond OSM est un style VECTORIEL, ses bâtiments sont des
 * polygones. Les aplatir en pixels pour les redeviner, c'est perdre volontairement ce qu'on cherche.
 * `queryRenderedFeatures` les rend en lng/lat, tels que la tuile les porte.
 *
 * ## Ce que ça n'est pas
 *
 * Ce module ne crée rien et ne décide rien : il rend des anneaux. Ce qu'on en fait — un aperçu à
 * trier, des formes d'éditeur — appartient à l'écran. Et il ne concerne QUE le fond vectoriel : le
 * satellite est du raster, il n'a pas de polygones à rendre (voir {@link queryBasemapBuildings}).
 *
 * ## La limite connue
 *
 * Une tuile vectorielle porte les bâtiments DÉCOUPÉS à ses bords, et un peu au-delà (le buffer,
 * 64/4096 de tuile, ~25 m à z14 sous nos latitudes). Un bâtiment à cheval sur un bord arrive donc
 * en deux morceaux, chacun fermé le long de la coupe, et un bâtiment situé dans la bande du buffer
 * est rendu DEUX FOIS — une fois par sa tuile, une fois par la voisine, coupé ailleurs.
 *
 * Les copies EXACTES sont retirées ({@link ringKey} : la clé est la géométrie, jamais l'id, qui est
 * celui de l'union de la tuile). Les copies COUPÉES différemment, qu'aucune clé géométrique ne peut
 * confondre, le sont par {@link withoutSmotheredRings} : le morceau tient dans le bâtiment entier et
 * longe ses murs, il n'ajoute donc rien de visible.
 *
 * Reste le cas d'un bâtiment plus profond que la bande commune aux deux tuiles : coupé des deux
 * côtés, il arrive en deux morceaux dont aucun ne contient l'autre, et les recoller demanderait une
 * vraie union de polygones. C'est rare, et l'opérateur le voit sur l'aperçu — le vert s'assombrit là
 * où deux formes se superposent.
 */

/** Un bâtiment lu sur le fond vectoriel. */
export type BasemapBuilding = {
  /**
   * Anneau extérieur en lng/lat, **non fermé** (le premier point n'est pas répété en dernier) :
   * c'est la convention d'un anneau de forme dans cet éditeur, la fermeture appartient au format
   * dans lequel l'appelant l'écrira.
   */
  ring: LngLat[];
  /** Cours intérieures, chacune au même format que `ring`. Absent quand le bâtiment n'en a pas. */
  holes?: LngLat[][];
  /** Identifiant porté par la tuile quand elle en porte un — traçabilité, jamais une clé métier. */
  sourceId?: string;
  /** Hauteur du sommet, en mètres (`render_height` des tuiles). Absente quand la tuile n'en donne pas. */
  height?: number;
  /** Hauteur de la base, en mètres (`render_min_height`), pour une partie posée en surplomb. */
  minHeight?: number;
};

/**
 * `source-layer` des bâtiments dans les styles OpenMapTiles, dont `positron` (OpenFreeMap) fait
 * partie. On interroge le SCHÉMA de la tuile, pas des identifiants de calques : les ids sont
 * propres à chaque style et changent avec lui, `building` est le nom de la couche de données.
 */
const BUILDING_SOURCE_LAYER = "building";

/**
 * Une géométrie GeoJSON telle que `queryRenderedFeatures` la rend, réduite à ce dont ce module a
 * besoin. Structurel plutôt qu'importé de MapLibre : {@link buildingsFromFeatures} se teste ainsi
 * sans carte, sans WebGL et sans jsdom.
 */
export type QueriedFeature = {
  id?: string | number;
  properties?: Record<string, unknown> | null;
  geometry: {
    type: string;
    coordinates: unknown;
  };
};

/**
 * Les calques du style qui peignent des bâtiments.
 *
 * `fill` et `fill-extrusion` : `positron` n'a pas de 3D, mais un style qui en aurait porterait ses
 * bâtiments dans le second type, et les ignorer rendrait une liste vide sans rien dire.
 */
export function buildingLayerIds(layers: readonly LayerSpecification[]): string[] {
  return layers
    .filter((layer) => layer.type === "fill" || layer.type === "fill-extrusion")
    .filter((layer) => "source-layer" in layer && layer["source-layer"] === BUILDING_SOURCE_LAYER)
    .map((layer) => layer.id);
}

/**
 * Anneaux extérieurs des bâtiments dont le CENTRE tombe dans la zone.
 *
 * Le critère du centre est celui de la sélection multiple de l'éditeur (voir
 * `handleSelectionDrawEnd`) : une forme à cheval sur le bord compte comme dehors, plutôt que
 * d'avoir à choisir un seuil de recouvrement arbitraire. Il est nécessaire ici parce que la carte
 * hors écran rend des tuiles ENTIÈRES : sans lui, on récolterait des bâtiments franchement hors de
 * ce que l'opérateur a entouré.
 *
 * Les trous (anneaux intérieurs) suivent leur bâtiment, dans `holes` : ils ne sont jamais rendus
 * comme des bâtiments à part, ce qui ferait un faux bâtiment au milieu d'une cour.
 */
export function buildingsFromFeatures(
  features: readonly QueriedFeature[],
  bbox: MercatorBbox,
): BasemapBuilding[] {
  const zone = normalizeBbox(bbox);

  return distinctBuildings(features, (ring) => isInside(polygonCentroid(ring), zone));
}

/**
 * Le bâtiment sous `point`, parmi des features lues à cet endroit.
 *
 * Le plus PETIT qui contient le point : une annexe bâtie dans la cour d'un grand bâtiment est
 * aussi à l'intérieur de son anneau extérieur, et c'est elle qu'on vise en cliquant dessus.
 */
export function buildingAtPoint(features: readonly QueriedFeature[], point: LngLat): BasemapBuilding | null {
  let best: BasemapBuilding | null = null;
  let bestArea = Infinity;

  const covers = (ring: LngLat[], holes: LngLat[][]) => pointInPolygon(point, ring) && !holes.some((hole) => pointInPolygon(point, hole));

  for (const building of distinctBuildings(features, covers)) {
    const area = ringArea(building.ring);
    if (area < bestArea) {
      best = building;
      bestArea = area;
    }
  }

  return best;
}

/**
 * Plus petit zoom auquel le style peint des bâtiments, `null` s'il n'en peint pas : en dessous, il
 * n'y a rien à lire sous le curseur.
 */
export function buildingsMinZoom(layers: readonly LayerSpecification[]): number | null {
  const ids = new Set(buildingLayerIds(layers));
  const zooms = layers.filter((layer) => ids.has(layer.id)).map((layer) => layer.minzoom ?? 0);

  return zooms.length ? Math.min(...zooms) : null;
}

/** Le bâtiment peint sous `position` sur une carte à l'écran — voir `MapLibreAdapter.basemapBuildingAt`. */
export function renderedBuildingAt(map: MlMap, position: LngLat): BasemapBuilding | null {
  const layers = buildingLayerIds(map.getStyle().layers ?? []);
  if (layers.length === 0) return null;

  const features = map.queryRenderedFeatures(map.project(position), { layers }) as unknown as QueriedFeature[];

  return buildingAtPoint(features, position);
}

/**
 * Un point garanti DANS l'anneau. Le centre géométrique ne l'est pas : celui d'un bâtiment en L ou
 * en U tombe dans sa cour. C'est le milieu du plus large segment intérieur de l'horizontale qui
 * coupe l'anneau à mi-hauteur — hors des cours, quand on les donne.
 */
export function interiorPoint(ring: LngLat[], holes: LngLat[][] = []): LngLat {
  const lats = ring.map(([, lat]) => lat);
  const y = (Math.min(...lats) + Math.max(...lats)) / 2;
  const crossings: number[] = [];

  for (const boundary of [ring, ...holes]) {
    for (let i = 0; i < boundary.length; i++) {
      const [ax, ay] = boundary[i]!;
      const [bx, by] = boundary[(i + 1) % boundary.length]!;
      if ((ay > y) !== (by > y)) crossings.push(ax + ((y - ay) * (bx - ax)) / (by - ay));
    }
  }

  crossings.sort((a, b) => a - b);

  let best: LngLat | null = null;
  let widest = -1;
  for (let i = 0; i + 1 < crossings.length; i += 2) {
    const width = crossings[i + 1]! - crossings[i]!;
    if (width > widest) {
      widest = width;
      best = [(crossings[i]! + crossings[i + 1]!) / 2, y];
    }
  }

  return best ?? polygonCentroid(ring);
}

/**
 * Vrai si `a` et `b` désignent le même bâtiment : lu deux fois (les deux lectures n'arrondissent pas
 * au même zoom, donc jamais à l'identique), ou l'un n'étant qu'un morceau de l'autre, coupé par une
 * tuile. Une annexe dans une cour reste un autre bâtiment : le grand ne tient pas dans elle.
 */
export function isSameBasemapBuilding(a: BasemapBuilding, b: BasemapBuilding): boolean {
  // Pré-filtre : deux bâtiments qui ne se touchent pas ne sont pas le même — et c'est presque
  // toujours le cas quand on compare tous ceux d'une zone entre eux.
  const boxA = boxOf(a.ring);
  const boxB = boxOf(b.ring);
  if (boxA.maxLng < boxB.minLng || boxB.maxLng < boxA.minLng || boxA.maxLat < boxB.minLat || boxB.maxLat < boxA.minLat) {
    return false;
  }

  const areaA = ringArea(a.ring);
  const areaB = ringArea(b.ring);
  const readTwice = Math.min(areaA, areaB) >= SAME_BUILDING_AREA_SHARE * Math.max(areaA, areaB)
    && pointInPolygon(interiorPoint(a.ring), b.ring)
    && pointInPolygon(interiorPoint(b.ring), a.ring);

  return readTwice || isSmotheredBy(a.ring, b.ring) || isSmotheredBy(b.ring, a.ring);
}

/** Deux lectures d'un même bâtiment ne diffèrent que d'un arrondi : leurs surfaces se valent. */
const SAME_BUILDING_AREA_SHARE = 0.8;

/** Ajoute `added` à `current` sans doublon ; d'un même bâtiment, la version la plus grande l'emporte. */
export function mergeBasemapBuildings(current: BasemapBuilding[], added: BasemapBuilding[]): BasemapBuilding[] {
  const merged = [...current];

  for (const building of added) {
    const index = merged.findIndex((other) => isSameBasemapBuilding(other, building));

    if (index < 0) merged.push(building);
    else if (ringArea(building.ring) > ringArea(merged[index]!.ring)) merged[index] = building;
  }

  return merged;
}

/**
 * Les bâtiments peints DANS le quadrilatère `corners` (lng/lat), sur une carte à l'écran : ceux dont
 * un point intérieur y tombe. Rapide, mais limité à ce qui est rendu — pour suivre la souris pendant
 * le tracé d'une zone, pas pour en lire le contenu complet (voir `queryBasemapBuildings`).
 */
export function renderedBuildingsIn(map: MlMap, corners: LngLat[]): BasemapBuilding[] {
  const layers = buildingLayerIds(map.getStyle().layers ?? []);
  if (layers.length === 0 || corners.length < 3) return [];

  const zone = boxOf(corners);
  const inZone = renderedCandidates(map, layers).filter((candidate) => {
    if (!boxesOverlap(candidate.box, zone)) return false;
    candidate.inside ??= interiorPoint(candidate.building.ring);
    return pointInPolygon(candidate.inside, corners);
  });

  return withoutSmotheredRings(inZone.map((candidate) => candidate.building));
}

type RenderedCandidate = { building: BasemapBuilding; box: Box; inside?: LngLat };

/**
 * Les bâtiments affichés, lus une fois tant que la carte ne bouge pas : une zone tracée à la souris
 * les redemande à chaque image, et les relire convertit à chaque fois tous les anneaux des tuiles.
 */
const renderedCandidatesByMap = new WeakMap<MlMap, { layersKey: string; candidates: RenderedCandidate[] }>();
const watchedMaps = new WeakSet<MlMap>();

function renderedCandidates(map: MlMap, layers: string[]): RenderedCandidate[] {
  const layersKey = layers.join("|");
  const cached = renderedCandidatesByMap.get(map);
  if (cached?.layersKey === layersKey) return cached.candidates;

  watchForChanges(map);

  const seen = new Set<string>();
  const candidates: RenderedCandidate[] = [];
  const features = map.queryRenderedFeatures({ layers }) as unknown as QueriedFeature[];

  for (const feature of features) {
    for (const { ring, holes } of polygonsOf(feature.geometry)) {
      if (ring.length < 3) continue;

      const key = ringKey(ring);
      if (seen.has(key)) continue;
      seen.add(key);

      candidates.push({ building: toBuilding(feature, ring, holes), box: boxOf(ring) });
    }
  }

  renderedCandidatesByMap.set(map, { layersKey, candidates });

  return candidates;
}

function watchForChanges(map: MlMap): void {
  if (watchedMaps.has(map)) return;
  watchedMaps.add(map);

  const forget = () => renderedCandidatesByMap.delete(map);
  map.on("move", forget);
  map.on("sourcedata", (event) => {
    const layers = map.getStyle().layers ?? [];
    const ids = new Set(buildingLayerIds(layers));
    const sources = layers.filter((layer) => ids.has(layer.id)).map((layer) => (layer as { source?: string }).source);
    if (sources.includes(event.sourceId)) forget();
  });
}

function boxesOverlap(a: Box, b: Box): boolean {
  return a.minLng <= b.maxLng && b.minLng <= a.maxLng && a.minLat <= b.maxLat && b.minLat <= a.maxLat;
}

/** Bâtiments retenus par `keep`, sans copie exacte ni morceau de tuile recouvert. */
function distinctBuildings(
  features: readonly QueriedFeature[],
  keep: (ring: LngLat[], holes: LngLat[][]) => boolean,
): BasemapBuilding[] {
  const seen = new Set<string>();
  const buildings: BasemapBuilding[] = [];

  for (const feature of features) {
    for (const { ring, holes } of polygonsOf(feature.geometry)) {
      // 3 sommets distincts au minimum : en dessous, ce n'est pas un polygone. Les tuiles portent
      // parfois des anneaux dégénérés en bord de découpe.
      if (ring.length < 3) continue;
      if (!keep(ring, holes)) continue;

      const key = ringKey(ring);
      if (seen.has(key)) continue;
      seen.add(key);

      buildings.push(toBuilding(feature, ring, holes));
    }
  }

  return withoutSmotheredRings(buildings);
}

/**
 * Écarte les anneaux qui **n'ajoutent aucune surface** : ceux qu'un anneau plus grand recouvre
 * déjà, et dont l'essentiel du contour est POSÉ SUR le contour de ce grand.
 *
 * ## Ce que ça règle
 *
 * Un bâtiment qui traverse un bord de tuile est rendu deux fois, coupé à un endroit différent par
 * chacune des deux tuiles (voir « La limite connue » en tête de module). Les deux morceaux ne sont
 * pas identiques — le dédoublonnage géométrique ({@link ringKey}) ne peut donc rien pour eux — mais
 * le plus petit est ENTIÈREMENT dans le plus grand, et il partage ses murs avec lui : il n'ajoute
 * qu'une coupe, jamais un bout de bâtiment. Sur un îlot parisien traversé par un bord de tuile,
 * cette passe ramène la surface peinte en double de 17 % à 3,5 %.
 *
 * ## Pourquoi ce critère-là, et pas « le petit est dans le grand »
 *
 * Parce que le test ne regarde que les anneaux extérieurs : celui d'un grand bâtiment couvre sa cour, et les annexes bâties dans cette cour tombent donc « dans » lui.
 * Sur les deux tuiles qui portent cet îlot, 1246 anneaux sont ainsi contenus dans un autre, et la
 * très grande majorité sont de VRAIS bâtiments — les écarter tous serait un remède pire que le mal.
 * Le contour partagé est ce qui les sépare : une annexe posée dans une cour ne longe pas les murs du
 * bâtiment qui l'entoure, un morceau coupé ne fait que ça.
 *
 * Le critère se lit d'ailleurs sans parler de tuiles : un anneau recouvert qui longe les murs de son
 * contenant ne peindrait rien de visible en plus. Ce qu'il écarte n'est jamais une forme que
 * l'opérateur verrait manquer.
 *
 * ## Ce qui reste
 *
 * Deux morceaux dont AUCUN ne contient l'autre — un bâtiment plus profond que la bande commune aux
 * deux tuiles, coupé des deux côtés. Ils se recouvrent en partie et restent deux formes ; c'est le
 * cas rare, et il demanderait une vraie union de polygones.
 */
function withoutSmotheredRings(buildings: BasemapBuilding[]): BasemapBuilding[] {
  // Du plus grand au plus petit : un morceau se juge contre le bâtiment entier, jamais l'inverse.
  // L'ordre de SORTIE, lui, reste celui d'arrivée — il suit l'ordre des tuiles, et le panneau de tri
  // numérote les bâtiments dans cet ordre.
  const ranked = buildings
    .map((building, index) => ({ index, ring: building.ring, box: boxOf(building.ring), area: ringArea(building.ring) }))
    .sort((a, b) => b.area - a.area);

  const kept: typeof ranked = [];
  const smothered = new Set<number>();

  for (const candidate of ranked) {
    // La bbox n'est qu'un pré-filtre : elle coûte quatre comparaisons là où le test de contour coûte
    // un parcours de sommets pour chaque anneau déjà retenu.
    const cover = kept.find(
      (other) => boxCovers(other.box, candidate.box) && isSmotheredBy(candidate.ring, other.ring),
    );

    if (cover) smothered.add(candidate.index);
    else kept.push(candidate);
  }

  return smothered.size === 0 ? buildings : buildings.filter((_, index) => !smothered.has(index));
}

/** Part des sommets à l'intérieur du contenant en dessous de laquelle on ne parle plus de morceau. */
const COVERED_SHARE = 0.95;

/** Part des sommets posés SUR le contour du contenant — c'est elle qui distingue un morceau coupé
 * d'une annexe bâtie dans une cour (voir {@link withoutSmotheredRings}). */
const SHARED_BORDER_SHARE = 0.9;

/**
 * Tolérance des tests « sur le contour », en degrés (~0,5 m).
 *
 * Elle n'est pas cosmétique : une tuile quantifie ses coordonnées sur `extent` pas (4096), soit
 * ~0,6 m à z14 sous nos latitudes, et deux tuiles voisines n'arrondissent pas un même mur au même
 * pas. Sans tolérance, les sommets partagés par les deux morceaux d'un bâtiment tombent à côté du
 * contour et le morceau passe pour un bâtiment de plus.
 */
const EDGE_TOLERANCE_DEGREES = 5e-6;

/** Vrai si `ring` est recouvert par `cover` ET longe ses murs — voir {@link withoutSmotheredRings}. */
function isSmotheredBy(ring: LngLat[], cover: LngLat[]): boolean {
  let inside = 0;
  let offBorder = 0;

  for (const point of ring) {
    if (isOnRing(point, cover)) {
      inside++;
      continue;
    }
    if ((ring.length - ++offBorder) / ring.length < SHARED_BORDER_SHARE) return false;
    if (pointInPolygon(point, cover)) inside++;
  }

  return inside / ring.length >= COVERED_SHARE;
}

/** Vrai si `point` est posé sur l'un des segments de `ring`, à {@link EDGE_TOLERANCE_DEGREES} près. */
function isOnRing(point: LngLat, ring: LngLat[]): boolean {
  for (let i = 0; i < ring.length; i++) {
    const from = ring[i]!;
    const to = ring[(i + 1) % ring.length]!;
    if (distanceToSegment(point, from, to) <= EDGE_TOLERANCE_DEGREES) return true;
  }

  return false;
}

function distanceToSegment(point: LngLat, from: LngLat, to: LngLat): number {
  const dx = to[0] - from[0];
  const dy = to[1] - from[1];
  const lengthSquared = dx * dx + dy * dy;
  const t = lengthSquared === 0 ? 0 : clamp01(((point[0] - from[0]) * dx + (point[1] - from[1]) * dy) / lengthSquared);

  return Math.hypot(point[0] - (from[0] + t * dx), point[1] - (from[1] + t * dy));
}

function clamp01(value: number): number {
  return Math.min(1, Math.max(0, value));
}

/** Aire du lacet de Gauss, en degrés² : sans unité utile, elle ne sert qu'à ORDONNER deux anneaux. */
function ringArea(ring: LngLat[]): number {
  let sum = 0;
  for (let i = 0; i < ring.length; i++) {
    const [x0, y0] = ring[i]!;
    const [x1, y1] = ring[(i + 1) % ring.length]!;
    sum += x0 * y1 - x1 * y0;
  }

  return Math.abs(sum) / 2;
}

type Box = { minLng: number; minLat: number; maxLng: number; maxLat: number };

function boxOf(ring: LngLat[]): Box {
  let minLng = Infinity;
  let minLat = Infinity;
  let maxLng = -Infinity;
  let maxLat = -Infinity;
  for (const [lng, lat] of ring) {
    if (lng < minLng) minLng = lng;
    if (lng > maxLng) maxLng = lng;
    if (lat < minLat) minLat = lat;
    if (lat > maxLat) maxLat = lat;
  }

  return { minLng, minLat, maxLng, maxLat };
}

/** Vrai si `inner` tient dans `outer`, à la tolérance des contours près. */
function boxCovers(outer: Box, inner: Box): boolean {
  return (
    inner.minLng >= outer.minLng - EDGE_TOLERANCE_DEGREES &&
    inner.minLat >= outer.minLat - EDGE_TOLERANCE_DEGREES &&
    inner.maxLng <= outer.maxLng + EDGE_TOLERANCE_DEGREES &&
    inner.maxLat <= outer.maxLat + EDGE_TOLERANCE_DEGREES
  );
}

/**
 * Lit les bâtiments du fond OSM couvrant `bbox`.
 *
 * La carte est montée HORS ÉCRAN et cadrée sur la zone ({@link withOffscreenBasemap}) plutôt
 * qu'interrogée sur la carte de l'opérateur : `queryRenderedFeatures` ne rend que ce qui est
 * RENDU, donc ce qui est dans le viewport courant. Sur la carte visible, un bâtiment de la zone
 * caché par le panneau, ou sorti du cadre depuis le tracé, manquerait — silencieusement.
 *
 * Rend une liste VIDE, sans erreur, pour tout fond qui n'a pas de polygones à donner (satellite,
 * aucun fond) : ce n'est pas une panne, c'est une réponse.
 */
export async function queryBasemapBuildings(params: {
  bbox: MercatorBbox;
  basemap: TileLayerName;
} & CaptureRegionOptions): Promise<BasemapBuilding[]> {
  if (params.basemap !== "osm") return [];

  const plan = planCapture(params.bbox, params);

  return withOffscreenBasemap(plan, params.basemap, (map) => {
    const layers = buildingLayerIds(map.getStyle().layers ?? []);
    if (layers.length === 0) return [];

    // Sans géométrie : tout le viewport, qui EST la zone (la carte est cadrée dessus). Le filtre
    // par centre reste nécessaire — les tuiles débordent du cadre.
    return buildingsFromFeatures(queryRendered(map, layers), plan.bbox);
  });
}

/** Isolé pour que le typage de MapLibre ne déborde pas dans la partie testable du module. */
function queryRendered(map: MlMap, layers: string[]): QueriedFeature[] {
  return map.queryRenderedFeatures(undefined, { layers }) as unknown as QueriedFeature[];
}

/** Polygones d'une géométrie `Polygon` ou `MultiPolygon`, anneaux ouverts ; rien pour le reste. */
function polygonsOf(geometry: QueriedFeature["geometry"]): { ring: LngLat[]; holes: LngLat[][] }[] {
  const polygons =
    geometry.type === "Polygon"
      ? [geometry.coordinates as LngLat[][]]
      : geometry.type === "MultiPolygon"
        ? (geometry.coordinates as LngLat[][][])
        : [];

  return polygons.flatMap(([outer, ...inner]) =>
    outer
      ? [{ ring: openRing(outer), holes: inner.map(openRing).filter((hole) => hole.length >= 3) }]
      : [],
  );
}

/** Retire le point de fermeture répété : voir {@link BasemapBuilding.ring}. */
function openRing(ring: LngLat[]): LngLat[] {
  const first = ring[0];
  const last = ring[ring.length - 1];
  if (ring.length > 1 && first && last && first[0] === last[0] && first[1] === last[1]) {
    return ring.slice(0, -1);
  }

  return ring;
}

function isInside(point: LngLat, zone: MercatorBbox): boolean {
  const [x, y] = lngLatToMercator(point);

  return x >= zone.minX && x <= zone.maxX && y >= zone.minY && y <= zone.maxY;
}

/**
 * Clé de dédoublonnage, **purement géométrique** : l'empreinte de l'anneau, arrondie au millionième
 * de degré (~0,1 m) — de quoi confondre deux rendus du même polygone sans jamais confondre deux
 * bâtiments voisins.
 *
 * L'identifiant de la feature n'y entre PAS, et c'est le point : sur les tuiles OpenFreeMap (z14 au
 * maximum), la couche `building` est FUSIONNÉE — une feature y est un multipolygone de centaines
 * d'anneaux, et son `id` est celui de l'union, propre à la tuile qui l'a produite. Un bâtiment rendu
 * par deux tuiles voisines arrive donc avec deux id différents pour une géométrie identique, et une
 * clé qui porterait l'id laisserait passer les deux. Les morceaux d'un bâtiment coupé par un bord
 * de tuile restent, eux, deux anneaux distincts : la géométrie seule suffit à les garder tous les
 * deux.
 */
function ringKey(ring: LngLat[]): string {
  return ring.map(([lng, lat]) => `${lng.toFixed(6)},${lat.toFixed(6)}`).join(";");
}

/** Les tuiles OpenMapTiles nomment l'identifiant OSM `osm_id` ; d'autres schémas n'en portent pas. */
function sourceIdOf(feature: QueriedFeature): string | undefined {
  const raw = feature.properties?.["osm_id"] ?? feature.id;

  return raw === undefined || raw === null ? undefined : String(raw);
}

function metersOf(feature: QueriedFeature, property: string): number | undefined {
  const value = Number(feature.properties?.[property]);

  return Number.isFinite(value) && value > 0 ? value : undefined;
}

function toBuilding(feature: QueriedFeature, ring: LngLat[], holes: LngLat[][]): BasemapBuilding {
  const sourceId = sourceIdOf(feature);
  const height = metersOf(feature, "render_height");
  const minHeight = metersOf(feature, "render_min_height");

  return {
    ring,
    ...(holes.length ? { holes } : {}),
    ...(sourceId === undefined ? {} : { sourceId }),
    ...(height === undefined ? {} : { height }),
    ...(minHeight === undefined ? {} : { minHeight }),
  };
}
